import React from 'react'
import type { MuiTheme as Theme, DialogProps } from '@paintscout/ui'
import { makeStyles, Dialog } from '@paintscout/ui'
import { useClientOptions } from '@paintscout/ui-business'
import { PresentationImbedPreview } from '../../PresentationPreview'
import { QuoteContextProvider, useQuote } from '../../../context'
import type { PresentationOptionV2, QuoteDocument } from 'paintscout'
import { getQuoteNextPresentation } from '@paintscout/util/builder'

export interface QuotePreviewDialogProps extends DialogProps {
  onConfirm?: (event: React.MouseEvent<HTMLElement>, selectedOptionalItems?: string[]) => void
  givenQuote?: QuoteDocument
  givenPresentation?: PresentationOptionV2
}

const useStyles = makeStyles(
  (_theme: Theme) => {
    return {
      dialogPaper: {
        overflow: 'hidden'
      },
      paperScrollPaper: {
        display: 'block'
      },
      presPreview: {
        height: '100vh'
      }
    }
  },
  { name: 'QuotePreviewDialog' }
)

function QuotePreviewDialog(props: QuotePreviewDialogProps) {
  const classes = useStyles(props)
  const { givenPresentation, givenQuote, onClose, onConfirm, ...baseDialogProps } = props
  const { options } = useClientOptions()

  // have two quote options so it works w/ and w/o context
  const { quote: usedQuote, estimator } = useQuote()
  const quote = givenQuote ? givenQuote : usedQuote
  // A presentation without pages crashes the preview renderer, so only trust
  // givenPresentation when it has pages; otherwise derive one from the quote.
  const presentation =
    (givenPresentation?.pages?.length ?? 0) > 0 ? givenPresentation : getQuoteNextPresentation({ options, quote })

  return (
    <Dialog
      classes={{ paper: classes.dialogPaper, paperScrollPaper: classes.paperScrollPaper }}
      forceFullScreen={true}
      scroll="body"
      fullWidth={true}
      PaperProps={{
        id: 'quote-preview-dialog-paper'
      }}
      onClose={onClose}
      {...baseDialogProps}
    >
      <QuoteContextProvider quote={{ ...quote, presentationTemplate: presentation }} estimator={estimator}>
        <PresentationImbedPreview
          border={'none'}
          loadingStyle={'cv'}
          classes={{ root: classes.presPreview }}
          handleAcceptClick={onConfirm ? (ev, selectedOptionalItems) => onConfirm(ev, selectedOptionalItems) : null}
          isPreviewAccept={true}
          onDismissDialog={onClose}
          presentation={presentation}
          usePresentationFromProp={true}
          quoteId={quote?._id}
        />
      </QuoteContextProvider>
    </Dialog>
  )
}

export default QuotePreviewDialog
